import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, X, Car as CarIcon, User as UserIcon, Building, Store, Hammer, FileText } from "lucide-react";

const KIND_OPTIONS = [
  { value: "project", label: "Proiect (șantier)", icon: Hammer, color: "#0044FF" },
  { value: "regie", label: "Regie", icon: FileText, color: "#A855F7" },
  { value: "magazie", label: "Magazie", icon: Store, color: "#0EA5E9" },
  { value: "birou", label: "Birou", icon: Building, color: "#8B5CF6" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Buton de adăugare rapidă de ore pentru admin.
 * Permite:
 *  - Selectarea tipului (Proiect/Regie/Magazie/Birou)
 *  - Selectarea proiectului/regiei dintr-o listă SAU crearea ad-hoc
 *  - Adăugarea unei liste de persoane cu ore + ore conduse fiecare
 *  - Salvarea per persoană prin PUT /persons/{pid}/calendar/day
 */
export default function QuickAdminHoursDialog({ open, onClose }) {
  const [date, setDate] = useState(todayISO());
  const [kind, setKind] = useState("project");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(""); // project_id sau regie_id
  const [projects, setProjects] = useState([]);
  const [regii, setRegii] = useState([]);
  const [persons, setPersons] = useState([]);
  const [rows, setRows] = useState([]); // [{person_id, person_name, hours, hours_driven, is_driver}]
  const [personSearch, setPersonSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state când dialog-ul se închide ca să nu apară persoane vechi la următoarea deschidere
      setDate(todayISO());
      setKind("project");
      setSearch("");
      setSelectedId("");
      setRows([]);
      setPersonSearch("");
      return;
    }
    (async () => {
      const [p, r, ps] = await Promise.all([
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/regii").catch(() => ({ data: [] })),
        api.get("/persons").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data || []);
      setRegii(r.data || []);
      // Filtrăm doar persoanele active (nu cei retrași)
      setPersons((ps.data || []).filter((x) => !x.retras));
    })();
  }, [open]);

  // Reset selecție când schimbi kind
  useEffect(() => {
    setSelectedId("");
    setSearch("");
  }, [kind]);

  const sourceList = kind === "project" ? projects : kind === "regie" ? regii : [];
  const filteredOptions = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return sourceList.slice(0, 20);
    return sourceList.filter((x) => (x.name || x.title || "").toLowerCase().includes(s)).slice(0, 20);
  }, [search, sourceList]);

  const needsTarget = kind === "project" || kind === "regie";
  const exactMatch = filteredOptions.some((x) => (x.name || x.title || "").toLowerCase() === search.trim().toLowerCase());
  const canCreateAdHoc = needsTarget && search.trim().length >= 3 && !exactMatch && !selectedId;

  const createAdHoc = async () => {
    setCreatingNew(true);
    try {
      const name = search.trim();
      const endpoint = kind === "project" ? "/projects" : "/regii";
      const body = kind === "project"
        ? { name, description: `Creat rapid din "Bagă ore" pe ${date}` }
        : { name, color: "#A855F7", start_date: date, indeterminate_end: true };
      const { data } = await api.post(endpoint, body);
      const id = data?.id || data?._id;
      if (!id) throw new Error("Server nu a returnat id");
      // Refetch listele
      if (kind === "project") {
        const r = await api.get("/projects");
        setProjects(r.data || []);
      } else {
        const r = await api.get("/regii");
        setRegii(r.data || []);
      }
      setSelectedId(id);
      toast.success(`${kind === "project" ? "Proiect" : "Regie"} "${name}" creat cu succes`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Eroare la creare ${kind}`);
    } finally { setCreatingNew(false); }
  };

  const addPerson = (pid) => {
    if (!pid || rows.some((r) => r.person_id === pid)) return;
    const p = persons.find((x) => x.id === pid);
    if (!p) return;
    setRows([...rows, {
      person_id: pid,
      person_name: p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim() || "—",
      hours: "8",
      hours_driven: "0",
      is_driver: false,
    }]);
    setPersonSearch("");
  };
  const removePerson = (pid) => setRows(rows.filter((r) => r.person_id !== pid));
  const updateRow = (pid, patch) => setRows(rows.map((r) => r.person_id === pid ? { ...r, ...patch } : r));

  const filteredPersonOptions = useMemo(() => {
    const s = personSearch.trim().toLowerCase();
    const already = new Set(rows.map((r) => r.person_id));
    return persons
      .filter((p) => !already.has(p.id))
      .filter((p) => !s || (p.nume_full || `${p.prenume || ""} ${p.nume || ""}`).toLowerCase().includes(s))
      .slice(0, 12);
  }, [persons, personSearch, rows]);

  const save = async () => {
    if (needsTarget && !selectedId) { toast.error(`Selectează ${kind === "project" ? "un proiect" : "o regie"} sau creează unul nou`); return; }
    if (rows.length === 0) { toast.error("Adaugă cel puțin o persoană"); return; }
    if (rows.some((r) => (parseFloat(r.hours) || 0) <= 0 && (parseFloat(r.hours_driven) || 0) <= 0)) {
      toast.error("Fiecare persoană trebuie să aibă ore lucrate sau ore conduse > 0"); return;
    }
    setSaving(true);
    try {
      const proj = kind === "project" ? projects.find((x) => x.id === selectedId) : null;
      const regie = kind === "regie" ? regii.find((x) => x.id === selectedId) : null;
      const label = proj?.name || regie?.name || (kind === "magazie" ? "Magazie" : "Birou");
      const color = regie?.color || (kind === "magazie" ? "#0EA5E9" : kind === "birou" ? "#8B5CF6" : "#0044FF");

      let ok = 0, failed = 0;
      for (const r of rows) {
        const hours = parseFloat(r.hours) || 0;
        const hours_driven = parseFloat(r.hours_driven) || 0;
        try {
          await api.put(`/persons/${r.person_id}/calendar/day`, {
            date,
            hourly_rate: 0, // backend folosește hourly_rate din profil dacă e 0
            entries: [{
              kind,
              project_id: kind === "project" ? selectedId : null,
              regie_id: kind === "regie" ? selectedId : null,
              label,
              hours,
              hours_driven,
              color,
            }],
          });
          ok++;
        } catch (e) {
          console.error(`Failed for ${r.person_name}`, e);
          failed++;
        }
      }
      if (failed === 0) {
        toast.success(`Ore salvate pentru ${ok} ${ok === 1 ? "persoană" : "persoane"} pe ${date}`);
        onClose();
      } else {
        toast.warning(`Salvat pentru ${ok} · Eșec pentru ${failed} persoane`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quick-admin-hours-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">⏱️ Bagă ore rapid</DialogTitle>
          <DialogDescription>
            Adaugă ore pentru una sau mai multe persoane pe o zi anume. Poți selecta un șantier existent sau crea unul nou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date + Kind */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Data *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="qh-date" />
            </div>
            <div>
              <Label>Tip alocare *</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="mt-1" data-testid="qh-kind"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <span className="flex items-center gap-2"><o.icon size={14} style={{ color: o.color }}/> {o.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Project/Regie selector + create ad-hoc */}
          {needsTarget && (
            <div>
              <Label>{kind === "project" ? "Proiect (șantier)" : "Regie"} *</Label>
              <div className="mt-1 relative">
                <Input
                  value={selectedId
                    ? (sourceList.find((x) => x.id === selectedId)?.name || sourceList.find((x) => x.id === selectedId)?.title || "")
                    : search}
                  onChange={(e) => { setSearch(e.target.value); setSelectedId(""); }}
                  placeholder={`Caută sau scrie un nume nou (min. 3 caractere)…`}
                  className="pr-20"
                  data-testid="qh-search"
                />
                {selectedId && (
                  <button
                    type="button"
                    onClick={() => { setSelectedId(""); setSearch(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                    title="Resetează"
                  ><X size={14}/></button>
                )}
              </div>
              {!selectedId && search.trim().length > 0 && (
                <div className="mt-1 border rounded-md bg-white shadow-sm max-h-60 overflow-y-auto">
                  {filteredOptions.length > 0 && filteredOptions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => { setSelectedId(o.id); setSearch(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 text-sm flex items-center gap-2"
                      data-testid={`qh-opt-${o.id}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: o.color || "#0044FF" }}/>
                      <span>{o.name || o.title}</span>
                    </button>
                  ))}
                  {canCreateAdHoc && (
                    <button
                      type="button"
                      onClick={createAdHoc}
                      disabled={creatingNew}
                      className="w-full text-left px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold text-sm flex items-center gap-2 border-t-2 border-emerald-200"
                      data-testid="qh-create-adhoc"
                    >
                      <Plus size={14}/> {creatingNew ? "Se creează…" : `Creează ${kind === "project" ? "proiect" : "regie"} nou: „${search.trim()}"`}
                    </button>
                  )}
                  {filteredOptions.length === 0 && !canCreateAdHoc && (
                    <div className="px-3 py-2 text-sm text-zinc-400 italic">Niciun rezultat (minim 3 caractere pentru creare nou).</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Persoane: adăugare */}
          <div>
            <Label className="flex items-center gap-2"><UserIcon size={14}/> Persoane * <span className="text-xs text-zinc-400 font-normal">(adaugă oricâte)</span></Label>
            <div className="mt-1 relative">
              <Input
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                placeholder="Caută persoană…"
                data-testid="qh-person-search"
              />
              {personSearch.trim().length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 border rounded-md bg-white shadow-md max-h-60 overflow-y-auto">
                  {filteredPersonOptions.length > 0 ? filteredPersonOptions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addPerson(p.id)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-zinc-100 last:border-b-0 text-sm flex items-center gap-2"
                      data-testid={`qh-person-opt-${p.id}`}
                    >
                      <Plus size={12} className="text-blue-600"/>
                      <span>{p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}</span>
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-zinc-400 italic">Nicio persoană găsită.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabel persoane adăugate */}
          {rows.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="qh-rows-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="p-2 text-left">Persoana</th>
                    <th className="p-2 text-right w-24">Ore lucrate</th>
                    <th className="p-2 text-right w-28">🚗 Ore conduse</th>
                    <th className="p-2 text-center w-16">Șofer</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.person_id} className={`border-t ${r.is_driver ? "bg-amber-50" : ""}`}>
                      <td className="p-2 font-medium">{r.person_name}</td>
                      <td className="p-2">
                        <Input
                          type="number" step="0.5" min="0" value={r.hours}
                          onChange={(e) => updateRow(r.person_id, { hours: e.target.value })}
                          className="h-8 text-right text-sm"
                          data-testid={`qh-row-hours-${r.person_id}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number" step="0.5" min="0" value={r.hours_driven}
                          onChange={(e) => updateRow(r.person_id, { hours_driven: e.target.value })}
                          className="h-8 text-right text-sm border-amber-300/60"
                          data-testid={`qh-row-driven-${r.person_id}`}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => updateRow(r.person_id, { is_driver: !r.is_driver })}
                          className={`p-1 rounded ${r.is_driver ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-400 hover:bg-zinc-200"}`}
                          title={r.is_driver ? "Este șofer" : "Marchează ca șofer"}
                          data-testid={`qh-row-driver-${r.person_id}`}
                        >
                          <CarIcon size={14}/>
                        </button>
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => removePerson(r.person_id)}
                          className="text-red-500 hover:bg-red-50 p-1 rounded"
                          title="Elimină"
                          data-testid={`qh-row-remove-${r.person_id}`}
                        ><X size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t mt-3">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="qh-cancel">Anulează</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qh-save">
            {saving ? "Se salvează…" : `Salvează ore (${rows.length} pers)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
